import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, startOfWeek, getWeek, subDays } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateEventColor } from '@/pages/scheduling/utils/colorUtils';
import { Download } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/components/ui/use-toast';
import GuardStatsMonthSelector from '@/pages/blank-page/components/GuardStatsMonthSelector';
import { exportAllGuardsShiftsToExcel } from '../services/exportService';
import { formatHoursAndMinutes } from '../services/utils/dateUtils';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useSupabaseUser } from '@/hooks/use-supabase-user';

interface AllTeamsMonthViewProps {
  currentMonth: Date;
}

interface TeamSchedule {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  team_id: string;
  team_name: string;
  assigned_guards: string[];
}

interface Guard {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
}

export const AllTeamsMonthView: React.FC<AllTeamsMonthViewProps> = ({ currentMonth: initialMonth }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [guards, setGuards] = useState<Guard[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(initialMonth);
  const [isExporting, setIsExporting] = useState(false);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Get month date range
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Fixed layout constants - increased guard column width
  const FIXED_GUARD_COLUMN_WIDTH = 300; // Increased from 200 to 300
  const DAY_COLUMN_WIDTH = 180;
  const ROW_HEIGHT = 80; // Fixed height for each row

  // Function to determine if a day is the start of a new week
  const isStartOfWeek = (date: Date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 1; // Monday
  };

  // Function to get week number for visual distinction
  const getWeekNumber = (date: Date) => {
    return getWeek(date, { weekStartsOn: 1 });
  };

  // Function to get background color for week distinction
  const getWeekBackgroundColor = (date: Date) => {
    const weekNumber = getWeekNumber(date);
    return weekNumber % 2 === 0 ? 'bg-muted/20' : 'bg-background';
  };

  // Sync scroll between header and content
  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (contentScrollRef.current && e.target !== contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current && e.target !== headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  // Handle month change
  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
  };

  // Handle export functionality
  const handleExportData = async () => {
    try {
      setIsExporting(true);
      
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      
      const result = await exportAllGuardsShiftsToExcel(monthStart, monthEnd);
      
      toast({
        title: t("exportSuccess") || "Export Successful",
        description: `${result.recordCount} ${t("recordsExported") || "records exported"}`,
      });
    } catch (error) {
      console.error("Export error:", error);
      
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: error instanceof Error 
          ? error.message 
          : (t("errorExportingData") || "Error exporting data"),
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Find previous day work for repo/leave shift creation
  const findPreviousDayWork = (guardId: string, targetDate: Date) => {
    const previousDay = subDays(targetDate, 1);
    const previousDayShifts = schedules?.filter(schedule => {
      return schedule.assigned_guards?.includes(guardId) && 
             isSameDay(fromLocalISOString(schedule.start_date), previousDay);
    });
    return previousDayShifts && previousDayShifts.length > 0 ? previousDayShifts[0] : null;
  };

  // Add repo shift based on previous day's work
  const addRepoShift = async (guardId: string, targetDate: Date) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast({
        variant: "destructive",
        title: t('noPreviousDayWork') || 'No previous day work found for this guard',
      });
      return;
    }

    try {
      const startDateTime = new Date(targetDate);
       startDateTime.setHours(13, 0, 0, 0); // 13:00
       
       const endDateTime = new Date(targetDate);
       endDateTime.setHours(13, 1, 0, 0); // 13:01

      const { error } = await supabase
        .from('team_schedules')
        .insert({
          title: 'ΡΕΠΟ',
          start_date: toLocalISOString(startDateTime),
          end_date: toLocalISOString(endDateTime),
          team_id: previousWork.team_id,
          assigned_guards: [guardId],
          created_by: user?.id || ''
        });

      if (error) throw error;

      toast({
        title: t('repoShiftCreated') || 'Repo shift created successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['all-guards-month-schedules'] });
    } catch (error) {
      console.error('Error creating repo shift:', error);
      toast({
        variant: "destructive",
        title: t('errorCreatingRepoShift') || 'Error creating repo shift',
      });
    }
  };

  // Add leave shift function
  const addLeaveShift = async (guardId: string, targetDate: Date, leaveType: string) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast({
        variant: "destructive",
        title: t('noPreviousDayWork') || 'No previous day work found for this guard',
      });
      return;
    }

    try {
      const startDateTime = new Date(targetDate);
       startDateTime.setHours(14, 1, 0, 0); // 14:01
       
       const endDateTime = new Date(targetDate);
       endDateTime.setHours(22, 0, 0, 0); // 22:00

      const { error } = await supabase
        .from('team_schedules')
        .insert({
          title: leaveType,
          start_date: toLocalISOString(startDateTime),
          end_date: toLocalISOString(endDateTime),
          team_id: previousWork.team_id,
          assigned_guards: [guardId],
          created_by: user?.id || ''
        });

      if (error) throw error;

      toast({
        title: t('leaveShiftCreated') || `${leaveType} shift created successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['all-guards-month-schedules'] });
    } catch (error) {
      console.error('Error creating leave shift:', error);
      toast({
        variant: "destructive",
        title: t('errorCreatingLeaveShift') || 'Error creating leave shift',
      });
    }
  };

  // Calculate total hours for a guard in the month
  const calculateGuardTotalHours = (guardId: string) => {
    if (!schedules) return 0;
    
    const guardSchedules = schedules.filter(schedule => 
      schedule.assigned_guards && schedule.assigned_guards.includes(guardId)
    );
    
    return guardSchedules.reduce((total, schedule) => {
      const startTime = fromLocalISOString(schedule.start_date);
      const endTime = fromLocalISOString(schedule.end_date);
      const hours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      return total + hours;
    }, 0);
  };

  // Get all guards from all teams
  const { data: guardsData } = useQuery({
    queryKey: ['all-guards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .eq('status', 'active')
        .order('full_name');
      
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (guardsData) {
      setGuards(guardsData.map(guard => ({
        ...guard,
        full_name: guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim()
      })));
    }
  }, [guardsData]);

  // Get all schedules for the month
  const { data: schedules, isLoading } = useQuery({
    queryKey: ['all-guards-month-schedules', format(monthStart, 'yyyy-MM'), format(monthEnd, 'yyyy-MM')],
    queryFn: async (): Promise<TeamSchedule[]> => {
      const { data, error } = await supabase
        .from('team_schedules')
        .select(`
          id,
          title,
          start_date,
          end_date,
          team_id,
          assigned_guards,
          teams!inner(name)
        `)
        .gte('start_date', monthStart.toISOString())
        .lte('start_date', monthEnd.toISOString())
        .order('start_date');

      if (error) throw error;

      return (data || []).map(schedule => ({
        ...schedule,
        team_name: schedule.teams?.name || 'Unknown Team'
      }));
    },
    enabled: guards.length > 0
  });

  // Generate title colors for all events
  const titleColors = React.useMemo(() => {
    const colors: Record<string, { backgroundColor: string; textColor: string }> = {};
    
    if (!schedules) return colors;
    
    const uniqueTitles = [...new Set(schedules.map(schedule => schedule.title))];
    
    uniqueTitles.forEach(title => {
      const firstSchedule = schedules.find(schedule => schedule.title === title);
      if (firstSchedule) {
        colors[title] = generateEventColor(title, fromLocalISOString(firstSchedule.start_date), theme);
      }
    });
    
    return colors;
  }, [schedules, theme]);

  // Get schedules for a specific guard and date
  const getGuardSchedulesForDay = (guardId: string, date: Date) => {
    if (!schedules) return [];
    
    return schedules.filter(schedule => 
      schedule.assigned_guards && 
      schedule.assigned_guards.includes(guardId) && 
      isSameDay(fromLocalISOString(schedule.start_date), date)
    );
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'HH:mm');
  };

  const formatGuardName = (guard: Guard) => {
    return guard.full_name || 'Unknown Guard';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('allTeamsMonthView') || 'All Teams - Month View'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter guards that have at least one shift
  const guardsWithShifts = guards.filter(guard => {
    return monthDays.some(day => getGuardSchedulesForDay(guard.id, day).length > 0);
  });

  return (
    <Card className="w-full max-w-full">
      <CardHeader className="bg-gradient-to-r from-muted/50 to-background">
        <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <span className="text-lg font-bold text-primary">{t('allGuardsMonthView') || 'All Guards - Month View'}</span>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Button
              onClick={handleExportData}
              disabled={isExporting || guardsWithShifts.length === 0}
              variant="outline"
              size="sm"
              className="hover-scale"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? (t('exporting') || 'Exporting...') : (t('export') || 'Export')}
            </Button>
            <GuardStatsMonthSelector
              selectedMonth={currentMonth}
              onMonthChange={handleMonthChange}
            />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="border rounded-lg overflow-hidden relative max-w-full">
          <div className="w-full overflow-x-auto">
            <div className="min-w-full">
              {/* Fixed Header for Days */}
              <div className="flex border-b bg-gradient-to-r from-muted to-muted/70 sticky top-0 z-20">
                {/* Fixed Guard Header */}
                <div 
                  className="flex-shrink-0 px-4 py-3 border-r bg-muted flex items-center sticky left-0 z-30 shadow-sm"
                  style={{ width: `${FIXED_GUARD_COLUMN_WIDTH}px` }}
                >
                  <span className="font-semibold text-sm text-primary">{t('guard') || 'Guard'}</span>
                </div>
                
                {/* Days Header - Ensure minimum width and responsive */}
                <div 
                  ref={headerScrollRef}
                  className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-muted"
                  onScroll={handleHeaderScroll}
                >
                  <div 
                    className="flex"
                    style={{ 
                      minWidth: `${Math.max(monthDays.length * 140, window.innerWidth - FIXED_GUARD_COLUMN_WIDTH)}px`,
                      width: `${monthDays.length * DAY_COLUMN_WIDTH}px`
                    }}
                  >
                    {monthDays.map((day) => (
                      <div 
                        key={day.toISOString()} 
                        className={`h-12 flex flex-col items-center justify-center text-xs font-medium border-r last:border-r-0 flex-shrink-0 transition-colors hover:bg-primary/5 ${
                          getWeekBackgroundColor(day)
                        } ${
                          isStartOfWeek(day) ? 'border-l-2 border-l-primary/40' : ''
                        }`}
                        style={{ 
                          width: `${Math.max(DAY_COLUMN_WIDTH, 140)}px`,
                          minWidth: '140px'
                        }}
                      >
                        <div className="font-medium">{format(day, 'EEE')}</div>
                        <div className="text-primary">{format(day, 'd')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Scrollable Content Area for All Guards */}
              <div 
                ref={contentScrollRef}
                className="h-[500px] overflow-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-muted"
                onScroll={handleContentScroll}
              >
                {guardsWithShifts.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="text-muted-foreground text-lg font-medium">
                      {t('noShiftsAssigned') || 'No shifts assigned to guards'}
                    </div>
                    <p className="text-sm text-muted-foreground/70 mt-2">
                      {t('scheduleGuardsToSeeData') || 'Schedule guards to see data here'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col relative">
                    {guardsWithShifts.map((guard, index) => {
                      const totalHours = calculateGuardTotalHours(guard.id);
                      
                      return (
                        <div key={guard.id} className={`flex border-b last:border-b-0 hover:bg-primary/5 relative transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
                          {/* Fixed Guard Name Column with Hours */}
                          <div 
                            className="flex-shrink-0 bg-background border-r px-4 py-4 flex flex-col justify-center sticky left-0 z-10 shadow-sm"
                            style={{ 
                              width: `${FIXED_GUARD_COLUMN_WIDTH}px`,
                              height: `${ROW_HEIGHT}px`
                            }}
                          >
                            <span className="font-semibold text-sm truncate" title={formatGuardName(guard)}>
                              {formatGuardName(guard)}
                            </span>
                            <span className="text-xs text-primary font-bold">
                              {formatHoursAndMinutes(Math.round(totalHours * 60))} total
                            </span>
                          </div>

                          {/* Schedule Content for this Guard */}
                          <div 
                            className="flex" 
                            style={{ 
                              minWidth: `${Math.max(monthDays.length * 140, window.innerWidth - FIXED_GUARD_COLUMN_WIDTH)}px`,
                              width: `${monthDays.length * DAY_COLUMN_WIDTH}px`
                            }}
                          >
                            {monthDays.map((day) => {
                              const daySchedules = getGuardSchedulesForDay(guard.id, day);
                              
                              return (
                                <ContextMenu key={day.toISOString()}>
                                  <ContextMenuTrigger asChild>
                                    <div 
                                      className={`p-1 border-r last:border-r-0 overflow-hidden flex-shrink-0 cursor-pointer hover:bg-primary/10 transition-all duration-200 ${
                                        getWeekBackgroundColor(day)
                                      } ${
                                        isStartOfWeek(day) ? 'border-l-2 border-l-primary/40' : ''
                                      }`}
                                      style={{ 
                                        width: `${Math.max(DAY_COLUMN_WIDTH, 140)}px`,
                                        minWidth: '140px',
                                        height: `${ROW_HEIGHT}px`
                                      }}
                                    >
                                      <div className="space-y-1 h-full overflow-y-auto scrollbar-thin scrollbar-thumb-primary/20">
                                        {daySchedules.map((schedule) => {
                                          const eventColors = titleColors[schedule.title] || { backgroundColor: '#e5e7eb', textColor: '#374151' };
                                          
                                          return (
                                            <div
                                              key={schedule.id}
                                              className="rounded-md px-2 py-1 border shadow-sm hover:shadow-md transition-shadow"
                                              style={{
                                                backgroundColor: eventColors.backgroundColor,
                                                color: eventColors.textColor,
                                                borderColor: eventColors.textColor + '30'
                                              }}
                                            >
                                          <div className="font-medium truncate text-xs" title={schedule.title}>
                                            {schedule.title}
                                          </div>
                                          <div className="text-xs opacity-75 truncate" title={schedule.team_name}>
                                            {schedule.team_name}
                                          </div>
                                            <div className="text-xs opacity-75 font-bold">
                                              {(['REPO','ΡΕΠΟ'].includes(schedule.title))
                                                ? '13:00 - 13:01'
                                                : (schedule.title.includes('ΑΔΕΙΑ') || schedule.title === 'ΜΗ ΕΡΓΑΣΙΑ'
                                                    ? '14:01 - 22:00'
                                                    : `${formatTime(schedule.start_date)} - ${formatTime(schedule.end_date)}`)
                                              }
                                            </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </ContextMenuTrigger>
                              <ContextMenuContent className="z-50 bg-background border">
                                <ContextMenuItem onClick={() => addRepoShift(guard.id, day)}>
                                  {t('addRepo') || 'Add Repo'}
                                </ContextMenuItem>
                                <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ')}>
                                  Add ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ
                                </ContextMenuItem>
                                <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ')}>
                                  Add ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ
                                </ContextMenuItem>
                                <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ')}>
                                  Add ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ
                                </ContextMenuItem>
                                <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΜΗ ΕΡΓΑΣΙΑ')}>
                                  Add ΜΗ ΕΡΓΑΣΙΑ
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
